import { SecretReference } from 'libs/end-to-end/src/types/secrets/secret-reference';
import React, { useCallback } from 'react';
import { useMemo } from 'react';
import { useState } from 'react';
import { OrganizationSecretItems } from 'libs/end-to-end/src/types/secrets/organization-secret-items';
import { GreenCheckmarkSvg } from 'apps/sigma2-ui/src/components/svg/GreenCheckmark';
import { ReadPatternHook } from 'apps/sigma2-ui/src/lib/hooks/useReadPattern';
import { OrganizationSecretSelectionDisplayMode } from 'apps/sigma2-ui/src/secrets/lib/types';
import { OrganizationSecretSelectionModalHook } from 'apps/sigma2-ui/src/secrets/lib/types';

export const useOrganizationSecretSelectionModal = ({
  secretReference,
  setSecretReference,
  onClose,
  listSecretsPattern,
}: {
  secretReference: SecretReference | null;
  setSecretReference: (secretReference: SecretReference | null) => void;
  onClose: () => void;
  organizationId: string;
  listSecretsPattern: ReadPatternHook<OrganizationSecretItems>;
}): OrganizationSecretSelectionModalHook => {
  const [displayMode, setDisplayMode] =
    useState<OrganizationSecretSelectionDisplayMode>('select');

  const [editSecretId, setEditSecretId] = useState<string>('');

  const transitionToEdit = useCallback((newEditSecretId: string) => {
    setEditSecretId(newEditSecretId);
    setDisplayMode('edit');
  }, []);

  const tryReloadListSecrets = useCallback(() => {
    if (
      listSecretsPattern.status === 'done' ||
      listSecretsPattern.status === 'error'
    ) {
      listSecretsPattern.reloadEntity();
    }
  }, [listSecretsPattern]);

  const transitionToSelect = useCallback(() => {
    setEditSecretId('');
    setDisplayMode('select');

    tryReloadListSecrets();
  }, [tryReloadListSecrets]);

  const transitionToCreate = useCallback(() => {
    setEditSecretId('');
    setDisplayMode('create');
  }, []);

  return useMemo(() => {
    return {
      modalTitle: 'Select Secret',
      editSecretId,
      secretRows:
        listSecretsPattern.status === 'done'
          ? listSecretsPattern.entity.secrets.map((secret) => {
              return {
                key: secret.secretId,
                columns: [
                  {
                    key: 'description',
                    columnType: 'text',
                    text: secret.secretDescription,
                  },
                  {
                    columnType: 'button',
                    key: 'editButton',
                    buttonText: 'Edit',
                    onClick: () => {
                      transitionToEdit(secret.secretId);
                    },
                  },
                  {
                    columnType: 'button',
                    key: 'selectButton',
                    buttonText: 'Select',
                    onClick: () => {
                      setSecretReference({
                        secretId: secret.secretId,
                      });
                      onClose();
                    },
                  },
                  {
                    columnType: 'custom',
                    key: 'currentSelectionIndicator',
                    ui:
                      secretReference &&
                      secretReference.secretId === secret.secretId ? (
                        <GreenCheckmarkSvg svg={{ className: 'w-4 h-4' }} />
                      ) : null,
                  },
                ],
              };
            })
          : [],
      goToCreateSecretButtonLabel: 'Create New Secret',
      goToCreateSecretButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          transitionToCreate();
        },
      },
      goToSelectSecretButtonLabel: 'Select Existing Secret',
      goToSelectSecretButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          transitionToSelect();
        },
      },
      displayMode,
      secretHeadings: ['Description', 'Edit', 'Select', 'Current Selection'],
      editOnComplete: () => {
        transitionToSelect();
      },
      createOnComplete: () => {
        transitionToSelect();
      },
      reloadButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          tryReloadListSecrets();
        },
      },
      reloadButtonLabel: 'Reload',
    };
  }, [
    displayMode,
    editSecretId,
    listSecretsPattern,
    onClose,
    secretReference,
    setSecretReference,
    transitionToCreate,
    transitionToEdit,
    transitionToSelect,
    tryReloadListSecrets,
  ]);
};
